import { BasicResponseDTO } from '@dto/basicResponse.dto';
import { LoginRequestDTO, LoginResponseDTO } from '@dto/session.dto';
import { SettingKey } from '@dto/settings.dto';
import { UserResponseDTO } from '@dto/usersResponse.dto';
import { parseBool } from '@dto/util/util';
import { Modal } from 'antd';
import axios, { AxiosError, HttpStatusCode } from 'axios';
import { useAppGlobStore } from 'src/appGlobalStore';
import { normalizeAutoplayInterval } from 'src/boxParts/dashboard/autoplay/autoplayConst';
import { getMenuFavorites } from 'src/component/breadcrumb/menuFavoritesService';
import { getEmails } from 'src/component/emailGroups/emailGroupService';
import { postApi } from 'src/util/apiCalls';
import { isBackendUnavailable, normalizeNetworkError } from 'src/util/axiosError';
import { DEFERRED_STATUS } from 'src/util/Deferred';
import { GLOB } from 'src/util/Glob';
import { ThemeUtil } from 'src/util/ThemeUtil';
import { API_URL } from '../data/Api';
import { DataService } from './data.service';
import { Log } from './log.service';
import { getSettings } from './settings.service';

export class Auth {

  private _auth = true;
  private _startupError: AxiosError<BasicResponseDTO> = null;

  constructor() {
    axios.interceptors.response.use(undefined, (error) => {
      if (!axios.isCancel(error) && error?.status !== HttpStatusCode.Unauthorized)
        console.error(error);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<BasicResponseDTO>;
        normalizeNetworkError(axiosError);
        switch (axiosError.response?.status) {
          case HttpStatusCode.Unauthorized:
            this.logOut();
            break;
          case HttpStatusCode.PaymentRequired: // license
          case HttpStatusCode.MethodNotAllowed: // demo
            Log.warn(axiosError.response?.data?.message || axiosError.response?.data?.error);
            throw new axios.Cancel(axiosError.response?.data?.error);
          default:
            // fallback notification
            if ((axiosError.config?.method ?? 'get').toLowerCase() !== 'get') {
              setTimeout(() => {
                const data = axiosError.response?.data;
                Log.error(data?.message || data?.error || axiosError.message || 'Request failed', axiosError);
              });
            }
            break;
        }
      }
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    });
    setTimeout(() => {
      void this.init();
    });
  }

  private setAuth(auth: boolean) {
    this._auth = auth;
    setTimeout(() => {
      GLOB.refresh();
    });
  }

  isLoggedIn() {
    return this._auth;
  }

  /** Set when start-up failed because the backend was down; the app shows a retry page instead of logging out */
  get startupError() {
    return this._startupError;
  }

  retryInit() {
    this._startupError = null;
    GLOB.xormonReady.reset();
    void this.init();
  }

  private async init(login = false) {

    this.setAuth(true);

    const pUser = axios.get<UserResponseDTO>(API_URL.USER).then(response => {
      GLOB.userInfo = { ...response.data.data };
      void GLOB.infoLoaded.promise.then(() => {
        if (!useAppGlobStore.getState().info.backend.demo || !ThemeUtil.isThemeStored()) {
          const cfg = GLOB.userInfo?.configuration;
          ThemeUtil.setTheme((cfg?.theme) ?? (cfg?.darkMode ? 'dark' : 'blue'));
        }
      });
    }, (reason: AxiosError) => Log.error('Failed to get user info!', reason));

    const pSet = getSettings(SettingKey.GLOB.PREFIX).then(response => {
      const settings = response.data.data;
      GLOB.graphTopItems = parseInt(settings.GLOB_TOP_ITEMS);
      GLOB.showDeletedItems = parseBool(settings.GLOB_SHOW_DELETED_ITEMS);
      GLOB.dashboardAutoplayInterval = normalizeAutoplayInterval(settings.GLOB_DASHBOARD_AUTOPLAY_INTERVAL);
      // The application's separators, which a profile that sets none of its own falls back to; an unset setting
      // keeps the default declared in Glob.
      GLOB.decimalSeparator = settings.GLOB_DECIMAL_SEPARATOR || GLOB.decimalSeparator;
      GLOB.csvSeparator = settings.GLOB_CSV_SEPARATOR || GLOB.csvSeparator;
    });

    const pBackend = DataService.checkVersionReload();

    return Promise.all([GLOB.infoLoaded.promise, pUser, pSet, GLOB.menuService.init(), GLOB.menuService.hwTypesLoaded.promise, pBackend]).then(() => {
      void getEmails().then(response => {
        useAppGlobStore.setState({ emailGroups: response.data.data });
      });
      void getMenuFavorites().then(response => {
        useAppGlobStore.setState({ menuFavorites: response.data });
      });
      setTimeout(() => {
        GLOB.xormonReady.resolve();
      });
    }, (reason: AxiosError) => {
      login && GLOB.xormonReady.reject(reason);
      if (isBackendUnavailable(reason)) {
        this._startupError = reason;
        GLOB.app.notification.destroy();
        GLOB.refresh();
        return;
      }
      setTimeout(() => {
        this.logOut();
      });
    });
  }

  async logIn(creds: LoginRequestDTO, url = API_URL.SESSION + '/login') {
    return postApi<LoginResponseDTO, LoginRequestDTO>(url, creds).then(response => {

      GLOB.showReleaseInfo = response?.data?.data?.showReleaseNote;
      return this.init(true);

    }, (error: AxiosError<BasicResponseDTO>) => {
      const invalidLogin = (error: string) => {
        Log.error(error);
        return error;
      };

      if (error.response)
        return invalidLogin(error.response.data.message);
      else if (error.message)
        return invalidLogin(error.message);

      return invalidLogin('No response was received!');
    });
  }

  logOut() {
    if (!this.isLoggedIn())
      return;

    axios.get<BasicResponseDTO>(API_URL.SESSION + '/logout').then(response => {
      // user logged out
      const ssoLogout = response.data.data;
      if (ssoLogout && !GLOB.isProdEnv()) {
        window.open(ssoLogout, '', 'height=400,width=500,scrollbars=1');
      } else
        GLOB.xormonReady.status === DEFERRED_STATUS.resolved && Log.warn('You have been logged out.');
    }, (reason: AxiosError) => Log.error('Failed to invalidate session!', reason));
    GLOB.selectedClass = GLOB.selectedType = GLOB.selectedItem = null;
    useAppGlobStore.setState({ changeLogModalTimestamp: 0 });
    GLOB.xormonReady.reject('logout');
    GLOB.xormonReady.reset();
    Modal.destroyAll();
    GLOB.app.notification.destroy(Log.STATIC_NOTIFICATION_KEY);
    this.setAuth(false);
  }
}
